import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Lightning, Trophy, Barbell, Timer, Fire, Heart, Ruler,
  ArrowsClockwise, CalendarBlank, User, ChartLineUp,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_TILES = [
  { key: "body_composition", label: "Body Comp",    icon: Ruler },
  { key: "mobility",         label: "Mobility",     icon: ArrowsClockwise },
  { key: "strength",         label: "Strength",     icon: Barbell },
  { key: "power",            label: "Power",        icon: Lightning },
  { key: "speed",            label: "Speed",        icon: Timer },
  { key: "agility",          label: "Agility",      icon: Fire },
  { key: "conditioning",     label: "Conditioning", icon: Heart },
];

const _fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};

// Render a payload dict as key: value rows, coping with nested dicts.
function renderPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  const rows = [];
  const push = (label, value) => {
    if (value === null || value === undefined || value === "") return;
    if (typeof value === "object") return; // nested — handled below
    rows.push([label, String(value)]);
  };
  Object.entries(payload).forEach(([k, v]) => {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      Object.entries(v).forEach(([k2, v2]) => push(`${k} · ${k2}`, v2));
    } else if (Array.isArray(v)) {
      push(k, `${v.length} entries`);
    } else {
      push(k, v);
    }
  });
  if (!rows.length) return <div className="label-tiny text-text-secondary">No data recorded.</div>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-baseline justify-between gap-3 border-b border-white/5 py-1.5 text-sm">
          <span className="label-tiny text-text-secondary text-[10px]">{label.replace(/_/g, " ").toUpperCase()}</span>
          <span className="tabular-nums text-right">{value}</span>
        </div>
      ))}
    </div>
  );
}

export default function PublicAssessment() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/public/assessment/${token}`)
      .then(({ data }) => { setData(data); setError(null); })
      .catch((e) => setError(e?.response?.data?.detail || "Unable to load assessment"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" data-testid="public-assessment-loading">
      <div className="label-tiny text-text-secondary">Loading assessment...</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="public-assessment-error">
      <div className="tactical-card p-8 max-w-md text-center">
        <div className="label-tiny text-brand-heat mb-2">// LINK UNAVAILABLE</div>
        <h1 className="font-heading text-3xl mb-2">Can&apos;t open this share</h1>
        <p className="text-text-secondary text-sm">{error}</p>
        <p className="text-text-secondary text-xs mt-4">Ask the coach to send you a fresh link.</p>
      </div>
    </div>
  );

  const s = data.summary || {};

  return (
    <div className="min-h-screen bg-surface-base p-6 lg:p-10" data-testid="public-assessment-page">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="tactical-card p-6 sm:p-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-tiny text-brand-accent mb-2">// EDGE TRAINING · ATHLETE ASSESSMENT</div>
            <h1 className="font-heading text-4xl sm:text-5xl">{data.athlete_name?.toUpperCase()}</h1>
            <div className="text-sm text-text-secondary mt-1.5">
              Coached by <span className="text-brand-accent font-medium">{data.coach_name}</span>
              {s.competition_date && (
                <> · Next competition: <span className="tabular-nums">{_fmtDate(s.competition_date)}</span>
                  {typeof s.days_to_competition === "number" && s.days_to_competition >= 0 && (
                    <span className="text-brand-heat"> ({s.days_to_competition}d)</span>
                  )}
                </>
              )}
            </div>
          </div>
          {s.performance_score !== undefined && s.performance_score !== null && (
            <div className="text-right">
              <div className="label-tiny text-text-secondary">PERFORMANCE SCORE</div>
              <div className="stat-number hot text-6xl">{s.performance_score}</div>
              <div className="label-tiny text-text-secondary">/ 100</div>
            </div>
          )}
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Tile icon={CalendarBlank} label="Last Assessment"   value={_fmtDate(s.last_assessment_date)} />
          <Tile icon={CalendarBlank} label="Next Assessment"   value={_fmtDate(s.next_assessment_date)} />
          <Tile icon={Trophy}        label="Strongest Quality" value={s.strongest_quality || "—"} />
          <Tile icon={ChartLineUp}   label="Focus Area"        value={s.weakest_quality || "—"} />
        </div>

        {/* Category payloads */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {CATEGORY_TILES.map((cat) => {
            const payload = data[cat.key];
            if (!payload || !Object.keys(payload).length) return null;
            return (
              <div key={cat.key} className="tactical-card p-5" data-testid={`public-cat-${cat.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <cat.icon size={16} weight="duotone" className="text-brand-accent" />
                  <h2 className="font-heading text-lg">{cat.label.toUpperCase()}</h2>
                </div>
                {renderPayload(payload)}
              </div>
            );
          })}
        </div>

        {/* History count */}
        {data.history?.length > 0 && (
          <div className="tactical-card p-4 flex items-center justify-between">
            <div className="text-sm">
              <span className="label-tiny text-brand-accent">// PROGRESS HISTORY · </span>
              <span className="tabular-nums text-brand-accent font-bold">{data.history.length}</span>
              <span className="text-text-secondary"> snapshot{data.history.length === 1 ? "" : "s"} recorded</span>
            </div>
            <div className="label-tiny text-text-secondary text-[10px]">
              Full graphs available inside the coach&apos;s workspace
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-text-secondary space-y-1">
          <div>Read-only share generated by <span className="text-brand-accent font-medium">Edge Training</span></div>
          <div>
            {data.expires_at && <>Link expires {_fmtDate(data.expires_at)} · </>}
            The coach can revoke this link at any time
          </div>
        </div>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value }) {
  return (
    <div className="tactical-card p-4" data-testid={`public-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 label-tiny text-text-secondary mb-1">
        <Icon size={12} weight="duotone" className="text-brand-accent" />
        <span>{label.toUpperCase()}</span>
      </div>
      <div className="font-heading text-xl truncate" title={value}>{value}</div>
    </div>
  );
}
